"use client";

import Script from "next/script";
import { useState, useEffect } from "react";

/**
 * Google Analytics 4 Component
 *
 * Dynamically loads GA4 based on configuration from the CMS backend API.
 * Only tracks in production environments.
 *
 * Features:
 * - Dynamic measurement ID from backend config
 * - Production-only tracking (skip localhost/preview)
 * - Respects Do Not Track browser preference
 * - Privacy-aware (anonymize IP)
 * - Local testing with NEXT_PUBLIC_FORCE_GA4=true
 */

const WEBSITE_JOBS_API = process.env.NEXT_PUBLIC_WEBSITE_JOBS_API;

// Production domains for RevealSite
const PRODUCTION_DOMAINS = [
  "revealsite.com",
  "www.revealsite.com",
  "revealsite.ai",
  "www.revealsite.ai",
  "revealsite-revamp.vercel.app",
];

const isProduction = (): boolean => {
  // Allow forcing GA4 for local testing
  if (process.env.NEXT_PUBLIC_FORCE_GA4 === "true") return true;

  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;

  return (
    PRODUCTION_DOMAINS.includes(hostname) ||
    hostname.endsWith(".vercel.app")
  );
};

interface GA4Config {
  measurement_id: string;
  track_page_views?: boolean;
  track_form_submissions?: boolean;
  track_scroll_depth?: boolean;
}

interface TrackingConfig {
  ga4?: GA4Config;
  privacy?: {
    anonymize_ip?: boolean;
    respect_dnt?: boolean;
    cookie_consent_required?: boolean;
  };
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export default function GoogleAnalytics() {
  const [measurementId, setMeasurementId] = useState<string | null>(null);
  const [trackingConfig, setTrackingConfig] = useState<TrackingConfig | null>(null);

  useEffect(() => {
    // Skip if no API configured
    if (!WEBSITE_JOBS_API) {
      return;
    }

    // Skip tracking on localhost/development
    if (!isProduction()) {
      return;
    }

    // Respect Do Not Track browser setting
    if (typeof navigator !== "undefined" && navigator.doNotTrack === "1") {
      return;
    }

    // Fetch tracking configuration from backend
    fetch(`${WEBSITE_JOBS_API}/config/tracking/`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.config?.ga4?.measurement_id) {
          setMeasurementId(data.config.ga4.measurement_id);
          setTrackingConfig(data.config);
        } else {
        }
      })
      .catch((error) => {
      });
  }, []);

  // Don't render anything until we have a measurement ID
  if (!measurementId) return null;

  const anonymizeIp = trackingConfig?.privacy?.anonymize_ip ?? true;

  return (
    <>
      {/* Google Analytics gtag.js */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            page_path: window.location.pathname,
            anonymize_ip: ${anonymizeIp}
          });
        `}
      </Script>
    </>
  );
}

export { WEBSITE_JOBS_API };
